import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Award, Copy, Download, ExternalLink, Loader2, ShieldCheck, ShieldX, Clock } from "lucide-react";
import AppNav from "@/components/app/AppNav";
import { api, formatApiError, API_BASE } from "@/api/client";

/**
 * Informed OS Academy — My Certificates (Phase 1e).
 *
 * Route: `/academy/certificates` (auth required).
 *
 * Surfaces:
 *  - Earned certificates (with download + shareable verification link)
 *  - Unclaimed-but-eligible enrollments (M4 "Claim your certificate" CTA)
 */
export default function MyCertificates() {
  const [state, setState] = useState({ status: "loading", items: [], unclaimed: [], error: null });
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    setState((p) => ({ ...p, status: "loading", error: null }));
    try {
      const r = await api.get("/academy/me/certificates");
      setState({
        status: "ready",
        items: r.data?.items || [],
        unclaimed: r.data?.unclaimed_enrollments || [],
        error: null,
      });
    } catch (err) {
      setState({ status: "error", items: [], unclaimed: [], error: formatApiError(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const claim = async (enrollmentId) => {
    setBusy((b) => ({ ...b, [enrollmentId]: true }));
    try {
      await api.post(`/academy/enrollments/${enrollmentId}/certificate`);
      await load();
    } catch (err) {
      setState((p) => ({ ...p, error: formatApiError(err) }));
    } finally {
      setBusy((b) => ({ ...b, [enrollmentId]: false }));
    }
  };

  const copyLink = (code) => {
    const url = `${window.location.origin}/academy/verify/${code}`;
    navigator.clipboard?.writeText(url);
  };

  return (
    <div data-testid="my-certificates-page" className="min-h-screen bg-v2-bg text-v2-text">
      <AppNav />
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <header className="mb-8">
          <p className="text-[11px] uppercase tracking-[0.18em] text-v2-text-muted">Informed OS Academy</p>
          <h1 className="mt-1 text-[32px] font-semibold text-v2-text" style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}>
            My Certificates
          </h1>
          <p className="mt-2 text-sm text-v2-text-muted max-w-2xl">
            Your completion certificates. Share your public verification link with employers or lenders — they can
            confirm authenticity without an account.
          </p>
        </header>

        {state.status === "loading" && (
          <div className="flex items-center gap-2 text-v2-text-muted" data-testid="my-certificates-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {state.status === "error" && (
          <div className="rounded-md border border-red-500/40 bg-red-500/5 p-4 text-red-300" data-testid="my-certificates-error">
            {state.error}
          </div>
        )}

        {state.status === "ready" && state.unclaimed.length > 0 && (
          <section data-testid="unclaimed-enrollments" className="mb-10">
            <h2 className="text-lg font-medium mb-3 text-v2-text">Ready to claim</h2>
            <ul className="space-y-3">
              {state.unclaimed.map((u) => (
                <li key={u.enrollment_id}
                    data-testid={`unclaimed-enrollment-${u.enrollment_id}`}
                    className="border border-v2-border rounded-lg p-4 flex items-center justify-between gap-4 bg-v2-surface">
                  <div>
                    <div className="text-v2-text font-medium">{u.program_title}</div>
                    <div className="text-xs text-v2-text-muted mt-0.5">
                      Completed {u.completed_at ? new Date(u.completed_at).toLocaleDateString() : "—"}
                    </div>
                  </div>
                  <button
                    data-testid={`claim-certificate-${u.enrollment_id}`}
                    disabled={!!busy[u.enrollment_id]}
                    onClick={() => claim(u.enrollment_id)}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#A78BFA] text-black text-sm font-medium hover:bg-[#9D7AEA] disabled:opacity-60 transition-colors"
                  >
                    {busy[u.enrollment_id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                    Claim your certificate
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {state.status === "ready" && state.items.length === 0 && state.unclaimed.length === 0 && (
          <div data-testid="my-certificates-empty" className="border border-dashed border-v2-border rounded-lg p-10 text-center text-v2-text-muted">
            <Award className="w-8 h-8 mx-auto mb-3 opacity-60" />
            <p>You haven't earned any certificates yet.</p>
            <p className="text-xs mt-1">Complete a program to see certificates here.</p>
          </div>
        )}

        {state.status === "ready" && state.items.length > 0 && (
          <section data-testid="my-certificates-list">
            <h2 className="text-lg font-medium mb-3 text-v2-text">Your certificates</h2>
            <ul className="grid md:grid-cols-2 gap-4">
              {state.items.map((c) => (
                <CertificateCard key={c.id} cert={c} onCopy={copyLink} />
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}

function CertificateCard({ cert, onCopy }) {
  const statusVisual = {
    valid: { icon: ShieldCheck, cls: "text-emerald-400 border-emerald-400/40 bg-emerald-400/5", label: "Valid" },
    revoked: { icon: ShieldX, cls: "text-red-400 border-red-400/40 bg-red-400/5", label: "Revoked" },
    expired: { icon: Clock, cls: "text-amber-400 border-amber-400/40 bg-amber-400/5", label: "Expired" },
  }[cert.status] || { icon: ShieldCheck, cls: "text-v2-text-muted border-v2-border", label: cert.status };
  const StatusIcon = statusVisual.icon;
  const downloadUrl = `${API_BASE}/academy/certificates/${cert.id}/download`;
  return (
    <li data-testid={`certificate-card-${cert.id}`} className="border border-v2-border rounded-lg p-5 bg-v2-surface">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-v2-text font-medium">{cert.program_title}</div>
          <div className="text-xs text-v2-text-muted mt-0.5">{cert.certificate_number}</div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border ${statusVisual.cls}`}>
          <StatusIcon className="w-3 h-3" /> {statusVisual.label}
        </span>
      </div>
      <dl className="text-xs text-v2-text-muted space-y-1 mb-4">
        <div className="flex justify-between"><dt>Issued</dt><dd className="text-v2-text">{cert.issued_at ? new Date(cert.issued_at).toLocaleDateString() : "—"}</dd></div>
        {cert.expires_at && (
          <div className="flex justify-between"><dt>Valid until</dt><dd className="text-v2-text">{new Date(cert.expires_at).toLocaleDateString()}</dd></div>
        )}
      </dl>
      <div className="flex flex-wrap gap-2">
        <a
          data-testid={`download-certificate-${cert.id}`}
          href={downloadUrl}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-v2-border text-v2-text text-xs hover:bg-white/5 transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Download PDF
        </a>
        <button
          data-testid={`copy-verify-link-${cert.id}`}
          onClick={() => onCopy(cert.verification_code)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-v2-border text-v2-text text-xs hover:bg-white/5 transition-colors"
        >
          <Copy className="w-3.5 h-3.5" /> Copy verify link
        </button>
        <Link
          data-testid={`open-verify-${cert.id}`}
          to={`/academy/verify/${cert.verification_code}`}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-v2-border text-v2-text text-xs hover:bg-white/5 transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Open verify page
        </Link>
      </div>
    </li>
  );
}
